import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { artistAdminList } from "@/services/firebase/artistHub";
import { PROFILE_STATUS_LABELS, VERIFIED_REASON_LABELS, ArtistProfileStatus } from "@/lib/artistAdmin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AdminErrorAlert, AdminStatusBadge } from "@/components/admin-ui";
import type { AdminStatusTone } from "@/components/admin-ui/admin-status-badge";

const profileTone: Record<ArtistProfileStatus, AdminStatusTone> = {
  published: "success",
  unpublished: "warning",
  none: "neutral",
};

/**
 * U6 藝人狀態摘要（artistAdminList([id])）: profile status, server-derived verification (+ reason), owners,
 * follower / works counts and featured order.
 */
export default function ArtistStatusSummary({ artistId }: { artistId: string }) {
  const query = useQuery({
    queryKey: ["artist-admin", "row", artistId],
    queryFn: () => artistAdminList([artistId]),
    retry: false,
  });
  const row = query.data?.items.find((r) => r.artistId === artistId) ?? null;

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>狀態摘要</CardTitle>
        <Button variant="outline" size="sm" disabled={query.isFetching} onClick={() => void query.refetch()}>
          {query.isFetching ? "讀取中…" : "重新整理"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {query.isPending && <p className="text-sm text-muted-foreground">讀取藝人狀態…</p>}
        {query.isError && <AdminErrorAlert message={query.error instanceof Error ? query.error.message : "無法取得藝人狀態"} />}
        {query.isSuccess && !row && <p className="text-sm text-muted-foreground">找不到此藝人的總覽資料。</p>}
        {row && (
          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <div className="space-y-1">
              <dt className="text-muted-foreground">專頁狀態</dt>
              <dd className="flex flex-wrap items-center gap-2">
                <AdminStatusBadge label={PROFILE_STATUS_LABELS[row.profileStatus] ?? row.profileStatus} tone={profileTone[row.profileStatus] ?? "neutral"} />
                {row.displayName && row.displayName !== row.name && <span className="text-muted-foreground">App 顯示名稱：{row.displayName}</span>}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-muted-foreground">認證徽章</dt>
              <dd className="flex flex-wrap items-center gap-2">
                <AdminStatusBadge label={row.verified ? "顯示認證徽章" : "未顯示認證徽章"} tone={row.verified ? "purple" : "neutral"} />
                {!row.verified && <span className="text-muted-foreground">原因：{VERIFIED_REASON_LABELS[row.verifiedReason] ?? row.verifiedReason}</span>}
              </dd>
              <dd className="text-xs text-muted-foreground">由伺服器判定：專頁已公開，且有未停權的有效藝人擁有者。</dd>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <dt className="text-muted-foreground">藝人擁有者（創作者帳號）</dt>
              {row.owners.length === 0 ? (
                <dd className="text-muted-foreground">
                  尚無擁有者，請到「
                  <Link to={`/actors/${artistId}/edit?tab=members`} className="underline underline-offset-2">
                    成員與開通
                  </Link>
                  」開通。
                </dd>
              ) : (
                <dd>
                  <ul className="space-y-1">
                    {row.owners.map((o) => (
                      <li key={o.uid} className="flex flex-wrap items-center gap-2">
                        <span className="break-all">{o.displayName || "（未設定名稱）"}</span>
                        {o.email && <span className="break-all text-muted-foreground">{o.email}</span>}
                        <span className="break-all text-xs text-muted-foreground">{o.uid}</span>
                        {o.suspended && <AdminStatusBadge label="已停權" tone="danger" />}
                      </li>
                    ))}
                  </ul>
                </dd>
              )}
            </div>
            <div className="space-y-1">
              <dt className="text-muted-foreground">追蹤數</dt>
              <dd className="text-base font-semibold">{row.followerCount.toLocaleString("zh-TW")}</dd>
            </div>
            <div className="space-y-1">
              <dt className="text-muted-foreground">作品</dt>
              <dd>
                <span className="text-base font-semibold">{row.worksCount}</span> 部已上架
                {row.unreleasedWorksCount > 0 && <span className="text-muted-foreground">（另有 {row.unreleasedWorksCount} 部未上架）</span>}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-muted-foreground">精選排序</dt>
              <dd className="flex flex-wrap items-center gap-2">
                {/* featuredOrder 9999 (FEATURED_DEFAULT) = not pinned; pinned ranks start at 1. */}
                {row.featuredOrder !== null && row.featuredOrder < 9999 ? (
                  <AdminStatusBadge label={`精選第 ${row.featuredOrder} 位`} tone="info" />
                ) : (
                  <span>未置頂（依追蹤數排序）</span>
                )}
                <Link to="/artists/featured" className="underline underline-offset-2">
                  調整精選藝人
                </Link>
              </dd>
            </div>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
